import EditIcon from "@mui/icons-material/Edit";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
	GALV_AREAS,
	getProductArea,
	PRODUCT_AREA_ASSET_ID,
	Product,
} from "@/shared/models/Product";
import { useBeamPositionsTableData } from "../hooks/useBeamPositionsTableData";
import { usePerforationsTableData } from "../hooks/usePerforationsTableData";
import { useSpecTableData } from "../hooks/useSpecTableData";
import { DrawingViewer } from "./DrawingViewer";
import { ProductLineageBar } from "./ProductLineageBar";

type Props = {
	product: Product | null;
	isLoading: boolean;
	onEdit: (productId: number, areaId: number) => void;
	onNavigateProduct?: (productId: number) => void;
};

export const ProductDetailPanel = ({
	product,
	isLoading,
	onEdit,
	onNavigateProduct,
}: Props) => {
	const navigate = useNavigate();

	const area = useMemo(
		() => (product ? getProductArea(product) : null),
		[product],
	);
	const areaAssetId = area ? PRODUCT_AREA_ASSET_ID[area] : null;

	const { columnDefs, rowData } = useSpecTableData({
		product,
		hasSpecsForArea: area != null,
	});

	const { columnDefs: beamPositionCols, rowData: beamPositionRows } =
		useBeamPositionsTableData(product);
	const { columnDefs: perforationCols, rowData: perforationRows } =
		usePerforationsTableData(product);

	if (isLoading) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!product) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography color="text.secondary">
					Select a product to view details
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					px: 2,
					pt: 2,
					pb: 1.5,
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					flexShrink: 0,
				}}
			>
				<Box>
					<Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
						{product.PartNumber}
					</Typography>
					{product.ExtendedPartNumber && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block" }}
						>
							Ext. Part #: {product.ExtendedPartNumber}
						</Typography>
					)}
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
						{[product.CustomerName, product.SectionName, product.GradeName]
							.filter(Boolean)
							.join("  ·  ")}
					</Typography>
					{product.CustomerPartNumber && (
						<Typography variant="caption" color="text.secondary">
							Cust. Part #: {product.CustomerPartNumber}
						</Typography>
					)}
				</Box>
				<Button
					variant="contained"
					color="primary"
					size="small"
					startIcon={<EditIcon />}
					onClick={() => {
						if (product.ProductId && areaAssetId) {
							onEdit(product.ProductId, areaAssetId);
						}
					}}
				>
					Edit
				</Button>
			</Box>

			<Divider />

			<Box sx={{ px: 2, pt: 1.5, pb: 1.25, flexShrink: 0 }}>
				<Paper
					variant="outlined"
					sx={{
						px: 1.5,
						py: 1.25,
						borderRadius: 2,
						backgroundColor: "rgba(0,0,0,0.015)",
					}}
				>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.75,
							fontSize: "0.62rem",
							letterSpacing: "0.08em",
							textTransform: "uppercase",
							color: "text.secondary",
							fontWeight: 700,
						}}
					>
						Lineage
					</Typography>
					<ProductLineageBar
						product={product}
						onNavigate={(id) => {
							if (onNavigateProduct) {
								onNavigateProduct(id);
							} else {
								navigate(`/products/${id}`);
							}
						}}
					/>
				</Paper>
			</Box>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					px: 2,
					pt: 1,
					pb: 2,
				}}
			>
				<Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							display: "flex",
							flexDirection: "column",
							gap: 3,
						}}
					>
						{area ? (
							<Box
								sx={{
									border: "1px solid rgba(0,0,0,0.12)",
									borderRadius: 1,
									"& .ag-header": { display: "none" },
								}}
							>
								<AgGridReact
									rowData={rowData}
									columnDefs={columnDefs}
									headerHeight={0}
									domLayout="autoHeight"
									suppressCellFocus
									suppressContextMenu
									gridOptions={{
										suppressNoRowsOverlay: true,
									}}
								/>
							</Box>
						) : (
							<Paper
								variant="outlined"
								sx={{
									py: 4,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Typography color="text.secondary">
									No specs for this product
								</Typography>
							</Paper>
						)}

						{area === "punch" && <Divider />}

						{area === "punch" && (
							<Box>
								<Typography
									variant="caption"
									sx={{
										display: "block",
										mb: 0.5,
										fontSize: "0.62rem",
										letterSpacing: "0.08em",
										textTransform: "uppercase",
										color: "text.secondary",
										fontWeight: 700,
									}}
								>
									Beam Positions
								</Typography>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										mb: 0.5,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										(Avenue [South] Side)
									</Typography>
									<Typography variant="caption" color="text.secondary">
										(River [North] Side)
									</Typography>
								</Box>
								<Box
									sx={{
										border: "1px solid rgba(0,0,0,0.12)",
										borderRadius: 1,
										height: 69,
									}}
								>
									<AgGridReact
										rowData={beamPositionRows}
										columnDefs={beamPositionCols}
										headerHeight={0}
										suppressCellFocus
										suppressContextMenu
										gridOptions={{ suppressNoRowsOverlay: true }}
									/>
								</Box>
							</Box>
						)}

						{area === "punch" && <Divider />}

						{area === "punch" && (
							<Box>
								<Typography
									variant="caption"
									sx={{
										display: "block",
										mb: 0.5,
										fontSize: "0.62rem",
										letterSpacing: "0.08em",
										textTransform: "uppercase",
										color: "text.secondary",
										fontWeight: 700,
									}}
								>
									Perforations
								</Typography>
								<Box
									sx={{
										border: "1px solid rgba(0,0,0,0.12)",
										borderRadius: 1,
									}}
								>
									<AgGridReact
										rowData={perforationRows}
										columnDefs={perforationCols}
										headerHeight={28}
										groupHeaderHeight={28}
										domLayout="autoHeight"
										suppressCellFocus
										suppressContextMenu
										gridOptions={{ suppressNoRowsOverlay: true }}
									/>
								</Box>
							</Box>
						)}
					</Box>

					{area && area !== "saw" && !GALV_AREAS.has(area) && (
						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								border: "1px solid rgba(0,0,0,0.12)",
								borderRadius: 1,
								overflow: "hidden",
							}}
						>
							<DrawingViewer
								productId={product.ProductId}
								drawingPath={product.DrawingPath ?? null}
								toolbarLeft={
									<TextField
										value={product.DrawingNumber ?? ""}
										label="Drawing Number"
										size="small"
										disabled
										sx={{ minWidth: 180 }}
									/>
								}
							/>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};
